import { useNavigate } from 'react-router-dom';
import { counterparties } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Copyable, Empty, Spinner } from '../components/ui';

/**
 * Counterparties — the external wallet addresses this project's rail has
 * interacted with, read-only. Rows are the real recipient addresses from onramp
 * orders (Dextopus bridge addresses are excluded server-side); clicking one
 * opens every interaction with it.
 */

const BASESCAN_ADDR = 'https://basescan.org/address/';

function addrDisplay(addr: string): string {
  return `${addr.slice(0, 10)}…${addr.slice(-6)}`;
}

function directionTone(direction: string): 'accent' | 'success' | 'neutral' {
  if (direction === 'both') return 'accent';
  if (direction === 'received') return 'success';
  return 'neutral';
}

export function Counterparties() {
  const navigate = useNavigate();
  const res = useResource(() => counterparties.list(), [], { refreshMs: 30000 });

  return (
    <>
      <div className="page-header">
        <h1>Counterparties</h1>
        <p>
          External wallet addresses you've delivered USDC to. Bridge/plumbing addresses are
          excluded — these are the real recipients from your onramp orders.
        </p>
      </div>

      {res.error && <Alert>{res.error}</Alert>}

      <Card title={res.data ? `${res.data.length} addresses` : 'Addresses'}>
        {res.loading ? (
          <Spinner />
        ) : !res.data?.length ? (
          <Empty>No counterparties yet — they appear once onramp orders deliver USDC.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Direction</th>
                  <th>Interactions</th>
                  <th>USDC sent</th>
                  <th>USDC received</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {res.data.map((cp) => (
                  <tr
                    key={cp.address}
                    onClick={() => navigate(`/counterparties/${encodeURIComponent(cp.address)}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <Copyable
                        value={cp.address}
                        display={addrDisplay(cp.address)}
                        href={BASESCAN_ADDR + cp.address}
                      />
                    </td>
                    <td>
                      <Badge tone={directionTone(cp.direction)}>{cp.direction}</Badge>
                    </td>
                    <td className="mono">{cp.interaction_count.toLocaleString()}</td>
                    <td className="mono">{cp.total_usdc_sent}</td>
                    <td className="mono">{cp.total_usdc_received}</td>
                    <td className="muted">{formatDateTime(cp.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
